// User-defined attributes: FLAGS — a badge or icon a rule raises on a record
// ("Urgent domestic", "LC pending", "Extended warranty").
//
// Flag rules come with the `uda_resolve` answer and are evaluated HERE, on the
// client, against the record's current values — one expression language for
// the whole platform (`@twasta/page-runtime/expr`, the port of the engine's
// `expr_eval`), with `{form.X}` / `{header.X}` references bound exactly as the
// engine binds them (`er.calc.bind_references`). A flag is a hint, never a
// gate: an expression that cannot be evaluated simply does not raise it.

import type { CSSProperties } from 'react'
import { PageIcon } from '../iconMap'
import type { UdaFlag } from './types'

export { evaluateFlagExpression, evaluateFlags } from './flagEval'

// ── look ───────────────────────────────────────────────────────────────────

/** A flag's colour name → the theme tokens it draws with. Data, not
 *  branches: a new colour is one entry here. */
export const FLAG_TONES: Record<string, { fg: string; bg: string }> = {
  accent: { fg: 'var(--color-accent)', bg: 'var(--color-accent-weak)' },
  error: { fg: 'var(--color-error)', bg: 'var(--color-error-weak)' },
  warn: { fg: 'var(--color-warning)', bg: 'var(--color-warning-weak)' },
  warning: { fg: 'var(--color-warning)', bg: 'var(--color-warning-weak)' },
  ok: { fg: 'var(--color-success)', bg: 'var(--color-success-weak)' },
  success: { fg: 'var(--color-success)', bg: 'var(--color-success-weak)' },
  info: { fg: 'var(--color-info)', bg: 'var(--color-info-weak)' },
}

export function flagTone(color: string | null | undefined): { fg: string; bg: string } {
  return FLAG_TONES[String(color || '').trim().toLowerCase()] || FLAG_TONES.accent
}

const DEFAULT_ICON = 'flag'

/** One flag as a pill with its icon and name (the record's title strip). */
export function FlagBadge({ flag }: { flag: UdaFlag }) {
  const tone = flagTone(flag.color)
  const style: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
    borderRadius: 999, padding: '3px 10px 3px 8px', color: tone.fg, background: tone.bg,
    whiteSpace: 'nowrap', lineHeight: 1.3,
  }
  return (
    <span style={style} title={flag.name || flag.key} data-tw-flag={flag.key}>
      <PageIcon name={flag.icon || DEFAULT_ICON} size={14} strokeWidth={2} fallback={<PageIcon name={DEFAULT_ICON} size={14} strokeWidth={2} />} />
      {flag.name || flag.key}
    </span>
  )
}

/** The raised flags as badges. Renders nothing when none is raised. */
export function FlagBadges({ flags }: { flags: readonly UdaFlag[] | null | undefined }) {
  if (!flags || flags.length === 0) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {flags.map(f => <FlagBadge key={f.key} flag={f} />)}
    </span>
  )
}

/** One flag as a round icon (a grid row, a list cell). */
export function FlagIcon({ flag, size = 26 }: { flag: UdaFlag; size?: number }) {
  const tone = flagTone(flag.color)
  return (
    <span title={flag.name || flag.key} aria-label={flag.name || flag.key} role="img"
      data-tw-flag={flag.key}
      style={{
        display: 'inline-grid', placeItems: 'center', width: size, height: size, borderRadius: '50%',
        color: tone.fg, background: tone.bg, flexShrink: 0,
      }}>
      <PageIcon name={flag.icon || DEFAULT_ICON} size={Math.round(size * 0.58)} strokeWidth={2}
        fallback={<PageIcon name={DEFAULT_ICON} size={Math.round(size * 0.58)} strokeWidth={2} />} />
    </span>
  )
}

/** The raised flags as icons, side by side. */
export function FlagIcons({ flags, size }: { flags: readonly UdaFlag[] | null | undefined; size?: number }) {
  if (!flags || flags.length === 0) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {flags.map(f => <FlagIcon key={f.key} flag={f} size={size} />)}
    </span>
  )
}
